import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { roadmapApi } from '../../api/roadmap.js';

/**
 * Gantt-style roadmap.
 *
 * Renders epics as horizontal SVG bars on a scrollable timeline. The right
 * edge of each bar is draggable to extend its end_date — the change is
 * applied optimistically on the client and PATCHed to the server on
 * mouseup.
 *
 * Implementation notes:
 *   - Pure SVG + math, no external Gantt library.
 *   - date-fns kept implicit; we use Date math + ISO strings throughout.
 *   - Today line is a red dashed vertical at the current date's x position.
 *   - Versions render as diamond markers on a thin band above the bars.
 */

const ZOOM_LEVELS = {
  week:    { label: 'Week',    daysPerCol: 1,  colWidth: 40 },
  month:   { label: 'Month',   daysPerCol: 7,  colWidth: 70 },
  quarter: { label: 'Quarter', daysPerCol: 30, colWidth: 90 },
  year:    { label: 'Year',    daysPerCol: 30, colWidth: 50 },
};

const FALLBACK_COLORS = ['#2563eb', '#16a34a', '#9333ea', '#f59e0b', '#dc2626', '#0ea5e9', '#db2777', '#0d9488'];

const ROW_HEIGHT = 36;
const ROW_GAP = 8;
const SIDEBAR_WIDTH = 220;
const HEADER_HEIGHT = 56;
const VERSION_BAND_HEIGHT = 24;

// ── Date helpers ──────────────────────────────────────────────────────
// Guard against null / undefined / Invalid Date — callers that receive
// null back should treat it as "no date" and skip rendering that bar.
const ymd = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
};
const parseYmd = (s) => {
  if (!s) return new Date(NaN);
  return new Date(s + 'T00:00:00Z');
};
const addDays = (date, n) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
};
const dayDiff = (a, b) => {
  const ya = ymd(a);
  const yb = ymd(b);
  if (!ya || !yb) return 0;
  return Math.round((parseYmd(ya) - parseYmd(yb)) / 86_400_000);
};

function monthLabel(date) {
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}
function shortMonth(date) {
  return date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
}
function dayLabel(date) {
  return date.toLocaleString('en-US', { weekday: 'short', timeZone: 'UTC' }).slice(0, 3);
}

// ── Component ─────────────────────────────────────────────────────────
export default function Roadmap() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState('month');
  const [hover, setHover] = useState(null); // { x, y, content }
  const [drag, setDrag] = useState(null); // { epicId, startX, originalEnd, currentEnd }
  const scrollerRef = useRef(null);

  const reload = useCallback(() => {
    roadmapApi.get(key).then(setData).catch(() => setData({ epics: [], versions: [], date_range: null }));
  }, [key]);

  useEffect(() => { reload(); }, [reload]);

  // ── Derive timeline geometry ──────────────────────────────────────
  const layout = useMemo(() => {
    if (!data) return null;
    const z = ZOOM_LEVELS[zoom];
    // Pad the range +/- 7 days for breathing room.
    const min = data.date_range?.min ? addDays(parseYmd(data.date_range.min), -7) : addDays(new Date(), -30);
    const max = data.date_range?.max ? addDays(parseYmd(data.date_range.max), 7) : addDays(new Date(), 60);
    const totalDays = Math.max(30, dayDiff(max, min));
    const cols = Math.ceil(totalDays / z.daysPerCol);
    const width = cols * z.colWidth;
    return { min, max, totalDays, cols, width, colWidth: z.colWidth, daysPerCol: z.daysPerCol, z };
  }, [data, zoom]);

  const xForDate = useCallback((isoDate) => {
    if (!layout || !isoDate) return 0;
    return (dayDiff(isoDate, layout.min) / layout.daysPerCol) * layout.colWidth;
  }, [layout]);

  // ── Today vertical line ─────────────────────────────────────────────
  const todayX = layout ? xForDate(ymd(new Date())) : 0;

  function scrollToToday() {
    if (!scrollerRef.current || !layout) return;
    const target = Math.max(0, todayX - 200);
    scrollerRef.current.scrollLeft = target;
  }

  // ── Epic drag-to-resize ─────────────────────────────────────────────
  const handleResizeMouseDown = (epic) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ epicId: epic.id, startX: e.clientX, originalEnd: epic.end_date, currentEnd: epic.end_date });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const days = Math.round((dx / (layout?.colWidth || 1)) * (layout?.daysPerCol || 1));
      if (!days && drag.currentEnd === drag.originalEnd) return;
      const newEnd = ymd(addDays(parseYmd(drag.originalEnd), days));
      setDrag((d) => ({ ...d, currentEnd: newEnd }));
    };
    const onUp = async () => {
      const { epicId, currentEnd, originalEnd } = drag;
      setDrag(null);
      if (currentEnd && currentEnd !== originalEnd) {
        // Optimistic local update
        setData((d) => ({
          ...d,
          epics: d.epics.map((e) => (e.id === epicId ? { ...e, end_date: currentEnd } : e)),
        }));
        try {
          await roadmapApi.updateEpicDates(epicId, { end_date: currentEnd });
        } catch {
          reload(); // revert on failure
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, layout, reload]);

  if (!data) return <div className="muted" style={{ padding: 24 }}>Loading roadmap…</div>;

  // ── Render header columns ──────────────────────────────────────────
  function renderHeaderColumns() {
    if (!layout) return null;
    const cells = [];
    let monthLabelY = 16;
    const seenMonths = new Set();
    for (let i = 0; i < layout.cols; i++) {
      const colDate = addDays(layout.min, i * layout.daysPerCol);
      const x = i * layout.colWidth;
      let label = '';
      if (zoom === 'week') label = dayLabel(colDate) + ' ' + colDate.getUTCDate();
      else if (zoom === 'month') label = `W${Math.ceil(colDate.getUTCDate() / 7)}`;
      else label = shortMonth(colDate);
      cells.push(
        <g key={i}>
          <line x1={x} y1={0} x2={x} y2={HEADER_HEIGHT + 5000}
                stroke="#e2e8f0" strokeWidth={1} />
          <text x={x + 4} y={HEADER_HEIGHT - 8} fontSize={11} fill="#475569">{label}</text>
        </g>
      );
      // Month banner above
      const ml = monthLabel(colDate);
      if (!seenMonths.has(ml)) {
        seenMonths.add(ml);
        cells.push(
          <text key={'m' + i} x={x + 4} y={monthLabelY} fontSize={12} fill="#0f172a" fontWeight={600}>
            {ml}
          </text>
        );
      }
    }
    return cells;
  }

  // ── Empty state ────────────────────────────────────────────────────
  if (data.epics.length === 0 && data.versions.length === 0) {
    return (
      <section style={{ padding: 24 }}>
        <h2>Roadmap</h2>
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <p className="muted">No epics with dates set for this project.</p>
          <p>Create epics with start and end dates to see them on the timeline.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="roadmap-view" style={{ padding: 0 }}>
      <header className="page-header" style={{ padding: '16px 20px' }}>
        <h2>Roadmap</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={zoom} onChange={(e) => setZoom(e.target.value)} aria-label="Zoom level">
            {Object.entries(ZOOM_LEVELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button className="btn ghost small" onClick={scrollToToday}>Today</button>
        </div>
      </header>

      <div className="roadmap-grid" style={{ display: 'grid', gridTemplateColumns: `${SIDEBAR_WIDTH}px 1fr` }}>
        {/* ── Sidebar: epic list ── */}
        <aside className="roadmap-sidebar" style={{ borderRight: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div style={{ height: HEADER_HEIGHT, borderBottom: '1px solid #e2e8f0', padding: '8px 12px', fontWeight: 600, fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'flex-end' }}>
            Epics
          </div>
          {data.epics.map((epic, idx) => {
            const color = epic.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
            return (
              <div
                key={epic.id}
                className="roadmap-row"
                style={{
                  height: ROW_HEIGHT + ROW_GAP,
                  padding: '0 12px',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (!scrollerRef.current) return;
                  scrollerRef.current.scrollLeft = Math.max(0, xForDate(epic.start_date) - 100);
                }}
              >
                <span style={{ width: 8, height: 8, background: color, borderRadius: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {epic.title}
                  </div>
                  <div style={{ height: 3, background: '#e2e8f0', borderRadius: 2, marginTop: 4 }}>
                    <div style={{ width: `${epic.progress_pct}%`, height: '100%', background: color, borderRadius: 2 }} />
                  </div>
                </div>
                <span className="chip" style={{ fontSize: 10, padding: '2px 6px' }}>{epic.issue_count}</span>
              </div>
            );
          })}
        </aside>

        {/* ── Right: scrollable SVG timeline ── */}
        <div ref={scrollerRef} style={{ overflowX: 'auto', overflowY: 'hidden' }}>
          {layout && (
            <svg
              width={layout.width}
              height={HEADER_HEIGHT + VERSION_BAND_HEIGHT + data.epics.length * (ROW_HEIGHT + ROW_GAP) + 16}
              style={{ display: 'block' }}
            >
              {/* Header */}
              <rect x={0} y={0} width={layout.width} height={HEADER_HEIGHT} fill="#f8fafc" />
              {renderHeaderColumns()}
              <line x1={0} y1={HEADER_HEIGHT} x2={layout.width} y2={HEADER_HEIGHT} stroke="#cbd5e1" strokeWidth={1} />

              {/* Today line */}
              {todayX >= 0 && todayX <= layout.width && (
                <line
                  data-testid="today-line"
                  x1={todayX} x2={todayX}
                  y1={0} y2={HEADER_HEIGHT + VERSION_BAND_HEIGHT + data.epics.length * (ROW_HEIGHT + ROW_GAP)}
                  stroke="#dc2626" strokeWidth={1.5} strokeDasharray="4 3"
                />
              )}

              {/* Version milestones band */}
              {data.versions.map((v) => {
                const x = xForDate(v.release_date);
                const y = HEADER_HEIGHT + VERSION_BAND_HEIGHT / 2;
                const fill = v.status === 'released' ? '#16a34a' : v.status === 'archived' ? '#94a3b8' : '#2563eb';
                return (
                  <g
                    key={v.id}
                    onMouseEnter={(e) => setHover({ x: e.clientX, y: e.clientY, content: `${v.name} · ${v.release_date} · ${v.status}` })}
                    onMouseLeave={() => setHover(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <polygon
                      points={`${x},${y - 8} ${x + 8},${y} ${x},${y + 8} ${x - 8},${y}`}
                      fill={fill} stroke="#0f172a" strokeWidth={0.5}
                    />
                  </g>
                );
              })}

              {/* Epic bars */}
              {data.epics.map((epic, idx) => {
                if (!epic.start_date || !epic.end_date) return null;
                const color = epic.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
                const startX = xForDate(epic.start_date);
                const liveEnd = drag && drag.epicId === epic.id ? drag.currentEnd : epic.end_date;
                const endX = xForDate(liveEnd);
                const w = Math.max(8, endX - startX);
                const y = HEADER_HEIGHT + VERSION_BAND_HEIGHT + idx * (ROW_HEIGHT + ROW_GAP) + ROW_GAP / 2;
                const showLabelInside = w > 80;
                const progressFillW = (w * (epic.progress_pct || 0)) / 100;
                return (
                  <g
                    key={epic.id}
                    className="epic-bar"
                    onMouseEnter={(e) => setHover({
                      x: e.clientX, y: e.clientY,
                      content: `${epic.title}\n${epic.start_date} → ${epic.end_date}\n${epic.progress_pct}% · ${epic.issue_count} issues`,
                    })}
                    onMouseLeave={() => setHover(null)}
                  >
                    {/* Background bar (full extent) */}
                    <rect
                      x={startX} y={y}
                      width={w} height={ROW_HEIGHT - 4}
                      rx={4} ry={4}
                      fill={color} fillOpacity={0.35}
                      stroke={color} strokeWidth={1}
                    />
                    {/* Progress fill (left portion, darker) */}
                    {progressFillW > 1 && (
                      <rect
                        x={startX} y={y}
                        width={progressFillW} height={ROW_HEIGHT - 4}
                        rx={4} ry={4}
                        fill={color} fillOpacity={0.85}
                      />
                    )}
                    {/* Title label */}
                    {showLabelInside ? (
                      <text x={startX + 8} y={y + (ROW_HEIGHT - 4) / 2 + 4} fontSize={12} fill="#fff" fontWeight={600} style={{ pointerEvents: 'none' }}>
                        {epic.title.length > Math.floor(w / 7) ? epic.title.slice(0, Math.floor(w / 7) - 1) + '…' : epic.title}
                      </text>
                    ) : (
                      <text x={endX + 6} y={y + (ROW_HEIGHT - 4) / 2 + 4} fontSize={12} fill="#0f172a" style={{ pointerEvents: 'none' }}>
                        {epic.title}
                      </text>
                    )}
                    {/* Right-edge resize handle */}
                    <rect
                      x={endX - 4} y={y - 1}
                      width={8} height={ROW_HEIGHT - 2}
                      fill="transparent"
                      style={{ cursor: 'col-resize' }}
                      onMouseDown={handleResizeMouseDown(epic)}
                      data-testid={`resize-${epic.id}`}
                    />
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>

      {/* Hover tooltip */}
      {hover && (
        <div
          style={{
            position: 'fixed',
            left: hover.x + 12,
            top: hover.y + 12,
            background: '#0f172a',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 4,
            fontSize: 11,
            whiteSpace: 'pre',
            pointerEvents: 'none',
            zIndex: 1000,
          }}
        >
          {hover.content}
        </div>
      )}
    </section>
  );
}
